'use client';

import posthog, { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { FC, PropsWithChildren, Suspense, useEffect } from 'react';
import { useUser } from './UserProvider';
import { useCookieConsent } from '@/app/stores/cookie-consent-store';
import { useNoTracking } from '@/app/hooks/useNoTracking';
import { isStandaloneMode } from '@/app/lib/utils';
import { storedHeardAbout } from '@/app/lib/heard-about';

const isPostHogEnabled = () =>
  !!process.env.NEXT_PUBLIC_POSTHOG_KEY &&
  (process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_FORCE_ENABLE_POSTHOG === 'true');

const POSTHOG_OPTIONS: Partial<PostHogConfig> = {
  api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  // Store PostHog state in localStorage only (default is 'localStorage+cookie'). The cookie is
  // set on our apex domain, so it gets sent up to Vercel on every same-origin request — inflating
  // Fast Data Transfer "incoming" for no server-side benefit (ingestion goes to api_host directly).
  persistence: 'localStorage',
  // App Router navigates client-side, so posthog-js's default (`true` — initial document load
  // only) records just the entry page of every session. 'history_change' adds pushState/
  // replaceState navigations; it fires only when the pathname actually changes.
  capture_pageview: 'history_change',
  opt_out_capturing_by_default: true,
  // Every link click is captured, to see how people navigate. A `data-ph-capture-attribute-nav` on a
  // region (header, footer, ...) becomes a `nav` property on clicks of any link inside it.
  autocapture: {
    css_selector_allowlist: ['[ph-autocapture]', 'a[href]'],
  },
  capture_exceptions: true,
  capture_performance: false,
  capture_dead_clicks: false,
  disable_session_recording: false,
  before_send: function (event) {
    // Filter out autocapture events from elements with ph-no-autocapture class
    if (event && event.event === '$autocapture') {
      const elementPath = event.properties.$elements_chain || '';
      if (elementPath.includes('ph-no-autocapture')) {
        return null; // Don't send this event
      }
    }
    return event;
  },
};

/*
  Isolated component so that useNoTracking (which calls useSearchParams) is inside a Suspense boundary
  and doesn't block static pre-rendering of the layout.

  PostHog is initialized on the first trackable page a visitor reaches, never on a no-tracking one:
  even opted out, an initialized client fetches its remote config and feature flags from PostHog.
*/
function PostHogInit() {
  const isNoTrackingPage = useNoTracking();
  const user = useUser();
  const { isAccepted, hasHydrated } = useCookieConsent();

  useEffect(() => {
    if (!isPostHogEnabled()) return;
    if (isNoTrackingPage) {
      if (posthog.__loaded) posthog.opt_out_capturing();
      return;
    }
    if (!posthog.__loaded) posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '', POSTHOG_OPTIONS);
    // PostHog remembers an opt-in across reloads, so it's only needed on Accept (each call sends an `$opt_in`
    // event), and withdrawing consent has to opt out explicitly.
    if (isAccepted) {
      if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing();
    } else if (hasHydrated && posthog.has_opted_in_capturing()) posthog.opt_out_capturing();
  }, [isAccepted, hasHydrated, isNoTrackingPage]);

  // Identify logged-in users and set user properties when user updates
  useEffect(() => {
    if (!isPostHogEnabled() || !posthog.__loaded || !user) return;
    // track whether this user is running from an installed mobile app
    posthog.register_for_session({ is_installed_mobile: isStandaloneMode() });

    // Identify only logged-in users, and key on the Supabase auth id: it's the one id that's stable
    // across devices and browsers, so PostHog can stitch their sessions into a single person.
    // user_str can't do that (it's a per-browser cookie) and rides along as a property instead, to
    // join back to `plays`. Anonymous visitors are deliberately left alone — any setPersonProperties
    // call would create a billable person profile keyed to a distinct_id that stitches nothing. That's
    // why a "how did you hear about us" answer given while signed out is only applied here.
    if (user.auth?.id) {
      posthog.identify(user.auth.id, {
        inkwell_user_str: user.user_str,
        email: user.auth.email,
        ...storedHeardAbout(),
      });
    }

    // Read search params directly from the URL — avoids needing useSearchParams and a Suspense boundary.
    const enabledFeatureName = new URLSearchParams(window.location.search).get('enable_feature');
    if (enabledFeatureName != null) {
      console.log('Setting user property for feature:', enabledFeatureName);
      posthog.setPersonProperties({ [`${enabledFeatureName}_enabled`]: true });
    }
    // isNoTrackingPage re-runs this after the init above first happens, on the first trackable page.
  }, [user, isNoTrackingPage]);

  return null;
}

export const PHProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  if (!isPostHogEnabled()) {
    return <>{children}</>;
  }

  // Children always render so that statically pre-rendered pages contain real HTML.
  // PostHog feature flags (flagsReady) are available to child components via the
  // PostHogProvider context; individual components can check them independently.
  return (
    <PostHogProvider client={posthog}>
      {/* PostHogInit uses useSearchParams via useNoTracking; Suspense keeps it out of static pre-rendering */}
      <Suspense>
        <PostHogInit />
      </Suspense>
      {children}
    </PostHogProvider>
  );
};
